import React from 'react';
import PropTypes from 'prop-types';

import { isValidAge } from '../../utils/Room';
import PlusCircleIcon from '../icons/PlusCircleIcon';
import MinusCircleIcon from '../icons/MinusCircleIcon';

import '../../static/less/global/paxtype_selector.less';

class PaxTypeSelector extends React.Component {
  constructor(props) {
    super(props);

    const { room, paxKey } = props;

    this.restrictions = {
      minPaxType: paxKey === 'adults' ? 1 : 0,
      maxPaxType: this.getMaxValue(room, paxKey),
    };

    this.updateControlValue = this.updateControlValue.bind(this);
  }

  getMaxValue(room, paxKey) {
    switch (paxKey) {
      case 'infants':
        return room.max_infants;
      case 'childs':
        return room.max_childs;
      default:
        return room.max_adults;
    }
  }

  isValidUpdate(control) {
    const {
      paxKey, paxNum, totalPax, room,
    } = this.props;
    const delta = control === 'min' ? -1 : 1;
    const updateValue = paxNum + delta;

    const isValidType = this.restrictions.maxPaxType >= updateValue && updateValue >= this.restrictions.minPaxType;
    const isValidTotal = paxKey === 'infants' || totalPax + delta <= room.max_pax;
    return isValidType && isValidTotal;
  }

  getStatusClass(control) {
    const isValidUpdate = this.isValidUpdate(control);
    return isValidUpdate ? '' : ' paxtypeselector__control--disabled';
  }

  updateControlValue(control) {
    const isValidUpdate = this.isValidUpdate(control);
    if (isValidUpdate) {
      const delta = control === 'min' ? -1 : 1;
      this.props.onClick(this.props.paxKey, delta);
    }
  }

  updateChildAge(control, index) {
    const delta = control === 'dec' ? -1 : 1;
    this.props.onAgeClick(this.props.paxKey, index, delta);
  }

  renderValidAge(age, index) {
    const { paxKey, hotel } = this.props;
    if (!isValidAge(hotel, paxKey, age)) {
      return null;
    }
    const canDecreaseAge = isValidAge(hotel, paxKey, age - 1);
    const canIncreaseAge = isValidAge(hotel, paxKey, age + 1);
    return (
      <div className="paxtypeselector__counter paxtypeselector__counter--ages"
           key={`age_${paxKey}_${index}`}>
        <div className={`paxtypeselector__control${canDecreaseAge ? '' : ' paxtypeselector__control--disabled'}`}
              onClick={() => this.updateChildAge('dec', index)}>
          <MinusCircleIcon />
        </div>
        <div className="paxtypeselector__number">
          {age} {this.context.t('years')}
        </div>
        <div className={`paxtypeselector__control${canIncreaseAge ? '' : ' paxtypeselector__control--disabled'}`}
              onClick={() => this.updateChildAge('inc', index)}>
          <PlusCircleIcon />
        </div>
      </div>
    );
  }

  renderChildrenAges() {
    if (this.props.paxKey === 'adults') {
      return null;
    }
    // We need to keep the indexes from the original array, so we filter invalid ages in the nested render
    return (
      <div className="paxtypeselector__ages small">
        <div className="px-1 mx-1">Select ages</div>
        {this.props.ages.map((age, index) => this.renderValidAge(age, index))}
      </div>
    );
  }

  renderPaxLabel() {
    let hint;
    let label;
    const { hotel } = this.props;
    switch (this.props.paxKey) {
      case 'childs':
        hint = `(${this.context.t('Ages')} ${hotel.min_child_age}-${hotel.max_child_age})`;
        label = this.context.t('Children');
        break;
      case 'infants':
        hint = `(${this.context.t('Ages under')} ${hotel.min_child_age})`;
        label = this.context.t('Infants');
        break;
      default:
        label = this.context.t('Adults');
        break;
    }
    return (
      <div className="paxtypeselector__name">
        {label}
        {hint
          && <div className="paxtypeselector__hint">
            {hint}
          </div>
        }
      </div>
    );
  }

  render() {
    return (
      <div className={this.props.typeExtra === 'onewaytohotel'
      || this.props.typeExtra === 'onewaytoairport'
      || this.props.typeExtra === 'roundtrip' ? 'flex mb-6' : 'paxtypeselector'}>

        <div className="flex m2 w-auto">
          <div className="flex-1 pr-1">
            {this.renderPaxLabel()}
          </div>
        </div>

        <div className={this.props.typeExtra === 'onewaytohotel'
        || this.props.typeExtra === 'onewaytoairport'
        || this.props.typeExtra === 'roundtrip' ? 'flex flex-row flex-col m2 w-2/4 right-0 pr-3' : 'flex m2 w-auto'}>
          <div className="paxtypeselector__counter">
            <div className={`paxtypeselector__control${this.getStatusClass('min')}`}
                 onClick={() => { this.updateControlValue('min'); }}>
              <MinusCircleIcon />
            </div>
            <div className="paxtypeselector__number">
              {this.props.paxNum}
            </div>
            <div className={`paxtypeselector__control${this.getStatusClass('max')}`}
                 onClick={() => { this.updateControlValue('max'); }}>
              <PlusCircleIcon />
            </div>
          </div>
        </div>

        {this.props.paxNum > 0 && this.renderChildrenAges()}
      </div>
    );
  }
}

PaxTypeSelector.propTypes = {
  hotel: PropTypes.object.isRequired,
  room: PropTypes.shape({
    max_adults: PropTypes.number.isRequired,
    max_childs: PropTypes.number.isRequired,
    max_infants: PropTypes.number.isRequired,
  }).isRequired,
  paxKey: PropTypes.string.isRequired, // PaxType for which the selector is
  paxNum: PropTypes.number.isRequired, // Number of paxes selected for this type
  totalPax: PropTypes.number.isRequired, // Total paxes of any type
  onClick: PropTypes.func.isRequired,
  onAgeClick: PropTypes.func, // Function for updating children ages
  ages: PropTypes.arrayOf(PropTypes.number), // ages for child types
};

PaxTypeSelector.defaultProps = {
  ages: [],
};

PaxTypeSelector.contextTypes = {
  t: PropTypes.func.isRequired,
};

export default PaxTypeSelector;
